import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FloatButton } from "antd";
import Header from "../../components/header";
import Footer from "../../components/footer";
import FAQ from "../../components/faq";
import CouponCode from "../../components/couponcode";
import BrandRecommendation from "../../components/brandrecommendation";
import { getCouponAPI, Offer } from "../../utils/API";
import AdPage from "../../components/AdPage";
import HelmetSeo from "../../components/HelmetSeo";
import {
  getBrandSeoMeta,
  getPartnerBoostSeoMeta,
  normalizeStoreKey,
} from "../../utils/seo";
import {
  getPartnerBoostBrandById,
  getPartnerBoostBrands,
  PARTNERBOOST_SOURCE,
} from "../../utils/partnerBoost";

const BrandDiscountCode: React.FC = () => {
  const [searchParams] = useSearchParams();
  const offer_id = searchParams.get("offer_id");
  const store = searchParams.get("store") || searchParams.get("amp;store");
  const source = searchParams.get("source");
  const brandType = searchParams.get("brand_type");

  const [brandData, setBrandData] = useState<Offer | null>(null);
  const [couponCodeData, setCouponCodeData] = useState<Offer[]>([]);
  const [brandRecommendationData, setBrandRecommendationData] = useState<
    Offer[]
  >([]);

  useEffect(() => {
    const fetchPageData = async () => {
      if (source === PARTNERBOOST_SOURCE && offer_id) {
        const { result, brand } = await getPartnerBoostBrandById(
          offer_id,
          brandType,
        );
        if (!result || !brand) {
          setBrandData(null);
          setCouponCodeData([]);
          setBrandRecommendationData([]);
          return;
        }

        setBrandData(brand);

        const resolvedBrandType = brandType || brand.standard_categories;
        if (resolvedBrandType) {
          const { brands } = await getPartnerBoostBrands(resolvedBrandType);
          const otherBrands = brands.filter(
            (item) => item.offer_id !== brand.offer_id,
          );
          setCouponCodeData([brand, ...otherBrands].slice(0, 7));
          setBrandRecommendationData(otherBrands);
        } else {
          setCouponCodeData([brand]);
          setBrandRecommendationData([]);
        }
        return;
      }

      const { result, offers } = await getCouponAPI();
      if (!result) return;

      const brandOffer = offers.find((offer) => offer.offer_id === offer_id);
      setBrandData(brandOffer || null);

      const targetStore =
        normalizeStoreKey(store) || normalizeStoreKey(brandOffer?.store);
      if (targetStore) {
        const storeOffers = offers.filter(
          (offer) => normalizeStoreKey(offer.store) === targetStore,
        );
        setCouponCodeData(storeOffers.slice(0, 7));
      } else {
        setCouponCodeData([]);
      }

      const randomData = [...offers].sort(() => 0.5 - Math.random());
      const seenStores = new Set<string>();
      const uniqueByStore: Offer[] = [];
      for (const offer of randomData) {
        const storeKey = offer.store?.trim().toLowerCase();
        if (!storeKey) continue;
        if (seenStores.has(storeKey)) continue;
        seenStores.add(storeKey);
        uniqueByStore.push(offer);
        if (uniqueByStore.length === 10) break;
      }
      setBrandRecommendationData(uniqueByStore);
    };

    fetchPageData();
  }, [offer_id, store, source, brandType]);

  // 处理品牌logo URL
  const getBrandLogoUrl = (brandLogo: string | undefined) => {
    if (!brandLogo) return "/images/white.png";
    return brandLogo?.replace("[size]", "200").replace("[format]", "png");
  };

  // 处理品牌名称，去掉.com后缀
  const getBrandName = (store: string | undefined) => {
    if (!store) return "";
    return store.replace(/\.com$/, "");
  };

  const brandDisplayName =
    brandData?.merchant_name || getBrandName(brandData?.store);

  const isPartnerBoost =
    source === PARTNERBOOST_SOURCE ||
    brandData?.source === PARTNERBOOST_SOURCE;
  const partnerBoostName =
    brandData?.merchant_name ||
    getBrandName(brandData?.store) ||
    getBrandName(store || undefined);
  const { title: seoTitle, description: seoDescription } = isPartnerBoost
    ? getPartnerBoostSeoMeta(partnerBoostName, brandData?.description)
    : getBrandSeoMeta(
        brandData?.store || store,
        brandData?.merchant_name || brandData?.title,
        brandData?.description,
      );

  const partnerBoostBrandType = brandType || brandData?.standard_categories;

  return (
    <>
      <HelmetSeo title={seoTitle} description={seoDescription} />
      <div className="relative w-full h-full flex flex-col justify-between bg-red-500">
        <Header />

        <AdPage />

        <main className="flex-1 w-full max-w-[1300px] mx-auto bg-red-500 h-auto">
          <div className="h-auto mx-auto">
            <div className="flex items-center justify-center">
              <div className="absolute z-0 top-[56px] md:top-[70px] w-full max-w-[1200px] mx-auto min-h-[265px] h-[244px] md:h-[400px]">
                <img
                  src="/images/coupon-home-pc.png"
                  alt=""
                  className="w-full h-full object-contain opacity-70"
                  loading="lazy"
                  decoding="async"
                  // fetch-priority="auto"
                  // referrerPolicy="no-referrer"
                  // crossOrigin="anonymous"
                  width="100%"
                  height="100%"
                />
              </div>
            </div>

            <div className="w-full relative z-1 mt-[120px] md:mt-[280px] h-auto px-4">
              <div className="w-full relative z-1 mt-[70px] md:mt-[280px]">
                <div className="max-w-[1200px] mx-auto">
                  <div className="px-3 py-4 md:p-4 md:max-w-[1200px] mx-auto md:border-none border-b border-[#fff9]">
                    <div className="flex items-center justify-between gap-4 bg-[#71000066] rounded-[10px] md:rounded-[15px] p-4">
                      <div className="rounded-[10px] md:rounded-[15px] border md:border-[1.5] border-[#ffffff1a] bg-white">
                        <img
                          src={getBrandLogoUrl(brandData?.brand_logo)}
                          alt={brandDisplayName || "brand logo"}
                          loading="lazy"
                          decoding="async"
                          width="74"
                          height="74"
                          onError={(e) => {
                            const target = e.currentTarget;
                            if (target.src.endsWith("/images/white.png"))
                              return;
                            target.src = "/images/white.png";
                          }}
                          className="w-20 h-20 md:w-[120px] md:h-[120px] rounded-[10px] md:rounded-[15px] object-contain transition-all hover:shadow-lg px-1"
                        />
                      </div>
                      <div className="flex-1 flex flex-col gap-1.5 md:gap-3">
                        <h1 className="font-bold text-base leading-5 md:text-[28px] md:leading-8 line-clamp-2 md:line-clamp-1 text-white">
                          {brandDisplayName || brandData?.title}
                        </h1>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col lg:flex-row gap-[30px] max-w-[1200px] mx-auto mt-4 md:mt-8 h-auto pb-0 md:px-5 lg:px-6">
                  <div className="w-full px-3.5 lg:px-0 lg:w-[336px] lg:min-w-[336px] lg:flex flex-col gap-6 h-auto">
                    <div className="w-full pb-6 lg:pb-0">
                      <div className="font-extrabold text-xl leading-[30px] line-clamp-1 text-white capitalize">
                        {brandDisplayName}
                      </div>
                      <div className="rounded-[8px] mt-4">
                        <div className="space-y-2 font-source font-medium p-3.5 border border-white rounded-[10px] text-white text-sm bg-white/5">
                          <p className="indent-1 first-letter:pl-3">
                            {seoDescription}
                          </p>
                        </div>
                      </div>
                    </div>
                    <BrandRecommendation
                      brandRecommendationData={brandRecommendationData.slice(
                        0,
                        9,
                      )}
                      detailLinkParams={
                        source === PARTNERBOOST_SOURCE && partnerBoostBrandType
                          ? {
                              source: PARTNERBOOST_SOURCE,
                              brandType: partnerBoostBrandType,
                            }
                          : undefined
                      }
                    />
                  </div>
                  <div className="flex-1 min-w-0 w-full lg:pt-[44px]">
                    <CouponCode couponCodeData={couponCodeData} />
                  </div>
                </div>

                <FAQ />
              </div>
            </div>
          </div>
        </main>

        <Footer />
      </div>
      <FloatButton.BackTop />
    </>
  );
};

export default BrandDiscountCode;
